import Api from "./Api";

const SERVICE_WORKER_URL = "/sw.js";

/**
 * iOS exposes Notification and PushManager only inside a home screen web app, so a plain
 * Safari tab reports "unsupported" no matter what. Callers use this to show an install
 * hint instead of a dead subscribe button.
 */
export function isStandalone() {
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        // iOS Safari predates display-mode and still only sets this proprietary flag.
        window.navigator.standalone === true
    );
}

export function isIos() {
    return (
        /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
        // iPadOS 13+ reports itself as a Mac, distinguishable only by touch support.
        (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
    );
}

export function isSupported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function getPermission() {
    return isSupported() ? Notification.permission : "unsupported";
}

/**
 * Explains, in the user's terms, why subscribing is not currently possible.
 * Returns undefined when subscribing should work.
 */
export function getBlockedReason() {
    if (isIos() && !isStandalone()) {
        return "NEEDS_INSTALL";
    }

    if (!isSupported()) {
        return "UNSUPPORTED";
    }

    if (Notification.permission === "denied") {
        return "DENIED";
    }

    return undefined;
}

export async function registerServiceWorker() {
    if (!("serviceWorker" in navigator)) {
        return undefined;
    }

    try {
        return await navigator.serviceWorker.register(SERVICE_WORKER_URL);
    } catch (error) {
        console.error("service worker registration failed", error);
        return undefined;
    }
}

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

export async function getExistingSubscription() {
    if (!isSupported()) {
        return undefined;
    }

    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
    if (!registration) {
        return undefined;
    }

    return (await registration.pushManager.getSubscription()) || undefined;
}

/**
 * Must be called from a real user gesture. iOS auto-rejects a permission prompt triggered
 * on load, and a denial can only be undone in the system Settings app.
 */
export async function subscribe() {
    const blockedReason = getBlockedReason();
    if (blockedReason) {
        return { success: false, reason: blockedReason };
    }

    const { response } = await Api.getPushPublicKey();
    if (!response?.publicKey) {
        return { success: false, reason: "SERVER_NOT_CONFIGURED" };
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
        return { success: false, reason: "DENIED" };
    }

    const registration = await registerServiceWorker();
    if (!registration) {
        return { success: false, reason: "UNSUPPORTED" };
    }

    // A pending registration has no pushManager yet on first install.
    await navigator.serviceWorker.ready;

    const subscription =
        (await registration.pushManager.getSubscription()) ||
        (await registration.pushManager.subscribe({
            // Silent push is not available on iOS; this flag is mandatory.
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(response.publicKey)
        }));

    await Api.subscribeToPush(subscription.toJSON());
    return { success: true };
}

export async function unsubscribe() {
    const subscription = await getExistingSubscription();
    if (!subscription) {
        return { success: true };
    }

    await Api.unsubscribeFromPush(subscription.endpoint);
    await subscription.unsubscribe();
    return { success: true };
}

/**
 * Mirrors the pending-approval count onto the home screen icon. Only available to
 * installed web apps, and a no-op everywhere else.
 */
export async function setBadge(count) {
    try {
        if (typeof navigator.setAppBadge !== "function") {
            return;
        }

        if (count > 0) {
            await navigator.setAppBadge(count);
        } else {
            await navigator.clearAppBadge();
        }
    } catch (error) {
        // Badging is decorative; never let it break a render.
    }
}
